/**
 * ONE's persisted browser device identity.
 *
 * Mechanics live in `@aegix/web-push`; this module owns ONE's
 * single-owner storage key. ONE uses its OWN key — the products may share
 * an eTLD+1 in some deployments and must never fight over one `device_id`
 * slot (never AIM's bare `device_id`, never SMS's `aegix_sms_device_id`).
 *
 * The value is sent as the `x-device-id` header on FCM register/deregister
 * so the backend's device-continuity rules (expired shared-session sweep +
 * cross-user hand-off) can key on a stable per-browser identity that
 * outlives the session. The ONE transport (`lib/deviceIdHeader.ts`) also
 * stamps the READ-ONLY view on outbound requests — `client.ky` calls plus
 * the allauth credential mints — so sessions get bound to the SAME
 * identity the push registration holds. Storage-unavailable fallback is
 * a session-stable ephemeral (`'stable'`).
 */
import { createDeviceIdStore } from '@aegix/web-push';

export const ONE_DEVICE_ID_STORAGE_KEY = 'aegix_one_device_id';

const store = createDeviceIdStore({
  storageKey: ONE_DEVICE_ID_STORAGE_KEY,
  ephemeralFallback: 'stable',
});

/**
 * The persisted device id, minting one on first use — registration always
 * has an identity to send.
 */
export const getOrCreateDeviceId = store.getOrCreate;

/**
 * Read-only view for callers that must NOT invent an identity (e.g.
 * deregistration). `null` when this browser has none persisted.
 */
export const getPersistedDeviceId = store.getPersisted;
